import React from 'react';
import PropTypes from 'prop-types'; // Import PropTypes for props validation
import { useForm } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import Modal from 'components/Modal';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import FormSelect from 'components/FormSelect';
import 'components/CreateGroups/styles.scss';
import { useGroupMutation } from 'utils/api/mutations'; // Add the fetchGroups function
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { toast } from 'react-toastify'; // <-- import toast here
import { toastStyle } from 'utils/helper'; // optional: your custom style
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { components } from 'react-select';
import { useFringeQuery } from 'utils/api/queries';

const CreateGroups = props => {
  const createGroupMutation = useGroupMutation.useCreateGroupMutation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const instanceType = queryParams.get('type') || 'budget';

  const { activeBudget } = useHomeScreenContext();
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    watch,
  } = useForm();

  const { data: frigneDetails } = useFringeQuery.useFringeDetailsQuery({
    filters: {
      instance_type: instanceType,
      active_id: activeBudget,
    },
    enabled: true,
  });

  const { setshowCreateGroupsModal, hasEditAccess } = useHomeScreenContext();

  const CustomMenuList = ({ children, ...rest }) => {
    return (
      <components.MenuList {...rest}>
        {children}
        <div
          style={{
            padding: '8px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
          }}
          onClick={() => setshowCreateGroupsModal(true)}
        >
          + Add Group
        </div>
      </components.MenuList>
    );
  };
  CustomMenuList.propTypes = {
    children: PropTypes.node.isRequired,
  };
  const onSubmit = data => {
    // Build the payload JSON step by step
    const payload = {};
    payload.name = data.name;
    payload.instance_type = instanceType;
    payload.budget = activeBudget;
    payload.fringe = data?.fringes?.map(fringe => fringe.value) || [];

    // Mutate using the created payload
    createGroupMutation.mutate(payload, {
      onSuccess: response => {
        const newGroup = response.data.data;
        props.onGroupCreated?.(newGroup);
        props?.closeFolder();
        reset();
        queryClient.invalidateQueries({
          predicate: query => query.queryKey[0] === 'get-group-fringe-detail',
        });
      },
      onError: error => {
        // Default message if nothing more specific is found
        let message =
          'An error occurred while creating the group. Please try again.';
        // If abbreviation error is present, show that instead
        if (error?.response?.data?.length) {
          message = error.response.data[0];
        }
        // Otherwise if there are any generic non_field_errors
        else if (error?.response?.data?.non_field_errors?.length) {
          message = error.response.data.non_field_errors[0];
        }

        toast.error(message, toastStyle);
        console.error('Error creating fringe group:', error);
      },
    });
  };

  return (
    <Modal
      onHide={props?.closeFolder}
      show={props?.showCreateGroupsModal}
      size="sm"
      className="create-budget"
      bodyClass="create-budget__body"
      centered
    >
      <h1 className="create-budget__body__header">Add New Fringe Group</h1>
      <Form className="overflow-hidden" onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          label="Group Name"
          type="text"
          error={errors?.name?.message}
          autoFocus={true}
          placeholder="Please enter a name"
          {...register('name', { required: 'Fringe name is required' })}
          disabled={instanceType === 'budget' && !hasEditAccess()}
        />
        <div className="group-select-container">
          <FormSelect
            label="Fringes"
            placeholder="Select fringes"
            options={(() => {
              const selectedFringes = watch('fringes') || [];
              const selectedIds = selectedFringes.map(f => f.value?.id);
              return (
                frigneDetails?.data?.data
                  ?.filter(fringe => !selectedIds.includes(fringe.id))
                  ?.map(fringe => ({
                    value: fringe,
                    label: fringe.display_name,
                  })) || []
              );
            })()}
            name="fringes"
            control={control}
            className="mb-5"
            isMulti={true}
            menuPlacement="top"
            menuPortalTarget={document.body}
            // components={{ MenuList: CustomMenuList }}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              menu: base => ({
                ...base,
                height: '200px',
                overflowY: 'auto',
              }),
              menuList: base => ({
                ...base,
                maxHeight: '200px',
                overflowY: 'auto',
              }),
              control: base => ({
                ...base,
                minHeight: '40px',
              }),
            }}
            isDisabled={instanceType === 'budget' && !hasEditAccess()}
          />
        </div>
        <div className="create-budget__body__action">
          <Button
            label="Cancel"
            type="reset"
            className="create-budget__action__cancel"
            variant="red-outlined"
            onClick={props?.closeFolder}
          />
          <Button
            label="Create"
            className="create-budget__action__confirm"
            type="submit"
            isDisabled={instanceType === 'budget' && !hasEditAccess()}
          />
        </div>
      </Form>
    </Modal>
  );
};

// Define propTypes for props validation
CreateGroups.propTypes = {
  closeFolder: PropTypes.func.isRequired,
  showCreateGroupsModal: PropTypes.bool.isRequired,
  onGroupCreated: PropTypes.func,
};
CreateGroups.defaultProps = {
  onGroupCreated: null,
};

export default CreateGroups;
